import { useEffect, useRef, useState, type ReactElement } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, WifiOff } from 'lucide-react';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../ui/form-dialog';
import {
  abastecimentoApi,
  ErroAbastecimento,
  type FaltaLiberacaoView,
  type PreflightLiberacao,
  type ResultadoLiberacao,
} from '../../lib/estoque/abastecimento-api';
import { formatarQuantidadeDecimal } from '../../lib/data-hora-operacional';
import { avisosLegiveis, TEXTO_AVISOS_NAO_BLOQUEIAM } from '../../lib/roteiros/consumo-tinta';

// Copywriting Contract (16-UI-SPEC): textos literais da liberação.
export const TEXTO_ESTRUTURA_TITULO = 'Materiais a definir.';
export const TEXTO_ESTRUTURA_CORPO = 'Não há quantidades previstas nesta estrutura. Registre a pendência antes de continuar.';
export const TEXTO_DESENHO_BLOQUEIO = 'Este item exige desenho técnico publicado antes da liberação.';
export const TEXTO_SEM_DESENHO = 'Sem desenho cadastrado';
export const TEXTO_REVISAO_LEGADA = 'Revisão não informada para este lote legado';
export const TEXTO_CONFLITO = 'Este registro foi alterado por outra pessoa. Recarregue para conferir a situação atual.';
export const TEXTO_INCERTO = 'Não foi possível confirmar o resultado desta ação. Consulte a situação atual antes de tentar novamente.';
export const TEXTO_FALHA_LEITURA = 'Não foi possível carregar os dados. Verifique a conexão e tente novamente.';
export const TEXTO_OFFLINE = 'Conexão necessária para confirmar esta ação.';
export const TEXTO_SEM_MATERIAIS = 'Nenhum material previsto informado.';
export const TEXTO_PERMISSAO = 'Você não tem permissão para liberar esta OP. O servidor também valida a autorização.';
const TEXTO_REQUER_ESTRUTURA = 'Confirme a ciência de Materiais a definir antes de liberar.';
const TEXTO_REQUER_FALTA = 'Confirme a ciência das faltas atuais antes de liberar.';
const TEXTO_DESCRICAO_AUSENTE = 'Descrição não informada';
const TEXTO_CODIGO_AUSENTE = 'Código não informado';

export type CtaLiberacao = 'Confirmar liberação' | 'Liberar mesmo assim';
export type FaseLiberacao = 'carregando' | 'erro-leitura' | 'conflito' | 'incerto' | 'pronto';
export type BloqueioLiberacao = 'DESENHO' | 'PERMISSAO' | 'OFFLINE' | null;
export type CienciaLiberacao = 'FALTA_MATERIAL' | 'MATERIAIS_A_DEFINIR';

/** Falha exibida no diálogo; `definitivo` carrega a mensagem do servidor ou do contrato. */
export interface ErroLiberacao {
  readonly tipo: 'leitura' | 'conflito' | 'incerto' | 'definitivo';
  readonly mensagem?: string;
}

export interface EntradaEstadoLiberacao {
  readonly carregando: boolean;
  readonly enviando: boolean;
  readonly online: boolean;
  readonly erro: ErroLiberacao | null;
  readonly preflight: PreflightLiberacao | null;
  readonly cienciaFalta: boolean;
  readonly cienciaEstrutura: boolean;
  readonly podeLiberar: boolean;
}

export interface EstadoLiberacao {
  readonly fase: FaseLiberacao;
  readonly bloqueio: BloqueioLiberacao;
  readonly cta: CtaLiberacao;
  readonly ctaHabilitada: boolean;
}

function desenhoBloqueia(preflight: PreflightLiberacao | null): boolean {
  return preflight?.revisao.desenhoObrigatorio === true && !preflight.revisao.desenhoDisponivel;
}

/** Ciência que a UI envia ao servidor: estrutura ausente prevalece sobre falta (mesma regra do servidor). */
export function cienciaDoPreflight(preflight: PreflightLiberacao | null): CienciaLiberacao | undefined {
  if (preflight?.estruturaAusente) return 'MATERIAIS_A_DEFINIR';
  if (preflight && preflight.faltas.length > 0) return 'FALTA_MATERIAL';
  return undefined;
}

/**
 * Estado derivado puro do diálogo. Conflito e resultado incerto desabilitam a CTA até a
 * situação atual ser relida: a confirmação nunca é reenviada sem reconciliação.
 */
export function estadoLiberacao(entrada: EntradaEstadoLiberacao): EstadoLiberacao {
  const { carregando, enviando, online, erro, preflight, cienciaFalta, cienciaEstrutura, podeLiberar } = entrada;
  const fase: FaseLiberacao = carregando
    ? 'carregando'
    : erro?.tipo === 'leitura' ? 'erro-leitura'
      : erro?.tipo === 'conflito' ? 'conflito'
        : erro?.tipo === 'incerto' ? 'incerto'
          : 'pronto';
  const bloqueio: BloqueioLiberacao = desenhoBloqueia(preflight) ? 'DESENHO' : !podeLiberar ? 'PERMISSAO' : !online ? 'OFFLINE' : null;
  const comFalta = (preflight?.faltas.length ?? 0) > 0;
  const cienciaOk = (!preflight?.estruturaAusente || cienciaEstrutura) && (!comFalta || cienciaFalta);
  return {
    fase,
    bloqueio,
    cta: comFalta ? 'Liberar mesmo assim' : 'Confirmar liberação',
    ctaHabilitada: preflight !== null && fase === 'pronto' && !enviando && bloqueio === null && cienciaOk,
  };
}

type FaltaComSolicitacao = FaltaLiberacaoView & { readonly solicitacaoPendente?: string | null };

/** O preflight atual não traz a solicitação pendente: só exibe quando o dado existir. */
function solicitacaoDaFalta(falta: FaltaLiberacaoView): string | null {
  const valor = (falta as FaltaComSolicitacao).solicitacaoPendente;
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

function revisaoLegada(preflight: PreflightLiberacao): boolean {
  return (preflight.revisao as { readonly legado?: boolean }).legado === true;
}

function textoRevisao(preflight: PreflightLiberacao): string {
  const { codigo, desenhoObrigatorio } = preflight.revisao;
  if (codigo) return `Revisão que será fixada: ${codigo}`;
  if (revisaoLegada(preflight)) return TEXTO_REVISAO_LEGADA;
  return desenhoObrigatorio ? 'Nenhuma revisão publicada aplicável' : TEXTO_SEM_DESENHO;
}

export interface LiberacaoCorpoProps {
  readonly preflight: PreflightLiberacao | null;
  readonly carregando?: boolean;
  readonly enviando?: boolean;
  readonly online?: boolean;
  readonly podeLiberar?: boolean;
  readonly erro?: ErroLiberacao | null;
  readonly cienciaFalta?: boolean;
  readonly cienciaEstrutura?: boolean;
  readonly onCienciaFalta?: (valor: boolean) => void;
  readonly onCienciaEstrutura?: (valor: boolean) => void;
  readonly onRecarregar?: () => void;
  readonly onTentarNovamente?: () => void;
}

function BannerErro({ erro, onRecarregar, onTentarNovamente }: { readonly erro: ErroLiberacao; readonly onRecarregar: () => void; readonly onTentarNovamente: () => void }): ReactElement {
  const texto = erro.tipo === 'leitura' ? TEXTO_FALHA_LEITURA : erro.tipo === 'conflito' ? TEXTO_CONFLITO : erro.tipo === 'incerto' ? TEXTO_INCERTO : (erro.mensagem ?? TEXTO_INCERTO);
  return <div role="alert" className="flex min-w-0 flex-wrap items-center gap-2 rounded border border-danger bg-card p-4 text-sm text-foreground">
    <AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-danger" />
    <span className="min-w-0 break-words font-semibold">Falha</span>
    <span className="min-w-0 break-words">{texto}</span>
    {erro.tipo === 'leitura' && <button type="button" onClick={onTentarNovamente} className="min-h-11 rounded border border-border px-4 font-semibold">Tentar novamente</button>}
    {(erro.tipo === 'conflito' || erro.tipo === 'incerto') && <button type="button" onClick={onRecarregar} className="min-h-11 rounded border border-border px-4 font-semibold">Recarregar situação atual</button>}
  </div>;
}

/** Corpo presentacional (sem portal): testável por renderToStaticMarkup. */
export function LiberacaoCorpo({
  preflight,
  carregando = false,
  enviando = false,
  online = true,
  podeLiberar = true,
  erro = null,
  cienciaFalta = false,
  cienciaEstrutura = false,
  onCienciaFalta = () => undefined,
  onCienciaEstrutura = () => undefined,
  onRecarregar = () => undefined,
  onTentarNovamente = () => undefined,
}: LiberacaoCorpoProps): ReactElement {
  const estado = estadoLiberacao({ carregando, enviando, online, erro, preflight, cienciaFalta, cienciaEstrutura, podeLiberar });
  return <div className="min-w-0 space-y-4">
    {carregando && <div aria-busy="true" role="status" data-slot="skeleton-liberacao" className="space-y-2">
      <span className="sr-only">Carregando situação da OP…</span>
      <div className="ui-skeleton h-8 w-full" />
      <div className="ui-skeleton h-32 w-full" />
    </div>}
    {erro && <BannerErro erro={erro} onRecarregar={onRecarregar} onTentarNovamente={onTentarNovamente} />}
    {preflight && <>
      <section className="min-w-0 rounded border border-border bg-muted p-4">
        <p className="min-w-0 break-words font-semibold text-foreground">{textoRevisao(preflight)}</p>
        {estado.bloqueio === 'DESENHO' && <p className="mt-2 flex min-w-0 gap-2 text-sm text-foreground"><AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-danger" /><span className="min-w-0 break-words">{TEXTO_DESENHO_BLOQUEIO}</span></p>}
      </section>
      <section className="min-w-0">
        <h3 className="text-sm font-semibold text-foreground">Materiais previstos</h3>
        {preflight.estruturaAusente
          ? <div className="mt-2 min-w-0 rounded border border-warning bg-card p-4 text-sm text-foreground">
            <p className="flex gap-2 font-semibold"><AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-warning" />{TEXTO_ESTRUTURA_TITULO}</p>
            <p className="mt-2 min-w-0 break-words">{TEXTO_ESTRUTURA_CORPO}</p>
          </div>
          : preflight.materiais.length === 0
            ? <p className="mt-2 text-sm text-muted-foreground">{TEXTO_SEM_MATERIAIS}</p>
            : <ul data-slot="lista-materiais" className="mt-2 max-h-64 min-w-0 space-y-2 overflow-y-auto">
              {preflight.materiais.map((material) => <li key={material.id} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-2 rounded border border-border bg-card p-4 text-sm text-foreground">
                <span className="min-w-0 break-words"><span className="font-semibold">{material.codigo?.trim() || TEXTO_CODIGO_AUSENTE}</span><span className="ml-2">{material.descricao?.trim() || TEXTO_DESCRICAO_AUSENTE}</span></span>
                <span className="text-right tabular-nums">{formatarQuantidadeDecimal(material.quantidade, material.unidade)}</span>
              </li>)}
            </ul>}
      </section>
      {avisosLegiveis(preflight.avisosConsumo).length > 0 && <section role="group" aria-label="Avisos de consumo" className="min-w-0 rounded border border-warning bg-card p-4">
        <h3 className="flex gap-2 text-sm font-semibold text-foreground"><AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-warning" />Avisos de consumo</h3>
        <ul className="mt-2 space-y-2 text-sm text-foreground">
          {avisosLegiveis(preflight.avisosConsumo).map((frase) => <li key={frase} className="min-w-0 break-words">{frase}</li>)}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">{TEXTO_AVISOS_NAO_BLOQUEIAM}</p>
      </section>}
      {preflight.faltas.length > 0 && <section role="group" aria-label="Falta identificada" className="min-w-0 rounded border border-warning bg-card p-4">
        <h3 className="flex gap-2 text-sm font-semibold text-foreground"><AlertTriangle aria-hidden="true" className="size-4 shrink-0 text-warning" />Falta identificada</h3>
        <ul className="mt-2 space-y-2 text-sm text-foreground">
          {preflight.faltas.map((falta, indice) => {
            const solicitacao = solicitacaoDaFalta(falta);
            return <li key={`${falta.material}-${falta.quantidade}-${indice}`} className="min-w-0 break-words">
              {falta.material}: faltam <span className="tabular-nums">{formatarQuantidadeDecimal(falta.quantidade, falta.unidade)}</span>
              {solicitacao && <span className="block text-muted-foreground">Solicitação pendente: {solicitacao}</span>}
            </li>;
          })}
        </ul>
        <label className="mt-4 flex min-h-11 items-center gap-2 text-sm font-semibold text-foreground"><input type="checkbox" checked={cienciaFalta} disabled={enviando} onChange={(event) => onCienciaFalta(event.target.checked)} />Confirmar ciência da falta</label>
      </section>}
      {preflight.estruturaAusente && <label className="flex min-h-11 items-center gap-2 rounded border border-warning bg-card p-4 text-sm font-semibold text-foreground"><input type="checkbox" checked={cienciaEstrutura} disabled={enviando} onChange={(event) => onCienciaEstrutura(event.target.checked)} />Confirmar ciência de Materiais a definir</label>}
      {(preflight.estruturaAusente || preflight.faltas.length > 0) && <p className="text-xs text-muted-foreground">A ciência é auditável; não cria reserva, saldo ou entrega.</p>}
    </>}
    {!podeLiberar && <p className="rounded border border-warning bg-card p-4 text-sm text-foreground">{TEXTO_PERMISSAO}</p>}
    {!online && <p id="liberacao-offline" role="alert" className="flex min-w-0 gap-2 rounded border border-warning bg-card p-4 text-sm text-foreground"><WifiOff aria-hidden="true" className="size-4 shrink-0 text-warning" /><span className="min-w-0 break-words">{TEXTO_OFFLINE}</span></p>}
  </div>;
}

/** Conteúdo sem portal para a prévia também poder ser verificado isoladamente. */
export function LiberacaoResumo({ preflight, cienciaFalta = false, cienciaEstrutura = false, onCienciaFalta = () => undefined, onCienciaEstrutura = () => undefined, podeLiberar = true }: {
  readonly preflight: PreflightLiberacao;
  readonly cienciaFalta?: boolean;
  readonly cienciaEstrutura?: boolean;
  readonly onCienciaFalta?: (valor: boolean) => void;
  readonly onCienciaEstrutura?: (valor: boolean) => void;
  readonly podeLiberar?: boolean;
}): ReactElement {
  return <LiberacaoCorpo preflight={preflight} cienciaFalta={cienciaFalta} cienciaEstrutura={cienciaEstrutura} onCienciaFalta={onCienciaFalta} onCienciaEstrutura={onCienciaEstrutura} podeLiberar={podeLiberar} />;
}

export type DesfechoLiberacao =
  | { readonly tipo: 'liberada'; readonly resultado: Extract<ResultadoLiberacao, { situacao: 'LIBERADA' }> }
  | { readonly tipo: 'requer-ciencia-estrutura' }
  | { readonly tipo: 'requer-ciencia-falta' }
  | { readonly tipo: 'bloqueada-desenho' }
  | { readonly tipo: 'conflito' }
  | { readonly tipo: 'incerto' }
  | { readonly tipo: 'definitivo'; readonly mensagem: string };

export interface GuardaEnvio { emAndamento: boolean }

export interface EntradaConfirmarLiberacao {
  readonly api: Pick<typeof abastecimentoApi, 'liberar'>;
  readonly opId: string;
  readonly ciencia: CienciaLiberacao | undefined;
  readonly chaveAtual: string | null;
  readonly guarda: GuardaEnvio;
  readonly gerarChave?: () => string;
}

function criarChave(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

/**
 * Envia a liberação uma vez por vez. `null` quando já há um envio em andamento (nada é chamado).
 * A chave da tentativa é mantida em rede/5xx (resultado incerto) e descartada em resposta
 * definitiva (sucesso, 4xx ou respostas de bloqueio/ciência). Nunca reenvia por conta própria.
 */
export async function confirmarLiberacao(entrada: EntradaConfirmarLiberacao): Promise<{ readonly desfecho: DesfechoLiberacao; readonly chave: string | null } | null> {
  const { api, opId, ciencia, chaveAtual, guarda, gerarChave = criarChave } = entrada;
  if (guarda.emAndamento) return null;
  guarda.emAndamento = true;
  const chave = chaveAtual ?? gerarChave();
  try {
    const resultado = await api.liberar(opId, ciencia);
    switch (resultado.situacao) {
      case 'BLOQUEADA_DESENHO': return { desfecho: { tipo: 'bloqueada-desenho' }, chave: null };
      case 'REQUER_CIENCIA_ESTRUTURA': return { desfecho: { tipo: 'requer-ciencia-estrutura' }, chave: null };
      case 'REQUER_CIENCIA_FALTA': return { desfecho: { tipo: 'requer-ciencia-falta' }, chave: null };
      default: return { desfecho: { tipo: 'liberada', resultado }, chave: null };
    }
  } catch (causa) {
    const status = causa instanceof ErroAbastecimento ? causa.status : null;
    if (status === 409) return { desfecho: { tipo: 'conflito' }, chave: null };
    if (status !== null && status < 500) {
      return { desfecho: { tipo: 'definitivo', mensagem: causa instanceof Error ? causa.message : 'Não foi possível liberar a OP.' }, chave: null };
    }
    return { desfecho: { tipo: 'incerto' }, chave };
  } finally {
    guarda.emAndamento = false;
  }
}

interface LiberacaoMateriaisProps {
  readonly opId: string;
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly podeLiberar: boolean;
  readonly preflight?: PreflightLiberacao;
  readonly onLiberada?: (resultado: Extract<ResultadoLiberacao, { situacao: 'LIBERADA' }>) => void;
}

function erroDoDesfecho(desfecho: DesfechoLiberacao): ErroLiberacao | null {
  switch (desfecho.tipo) {
    case 'bloqueada-desenho': return { tipo: 'definitivo', mensagem: TEXTO_DESENHO_BLOQUEIO };
    case 'requer-ciencia-estrutura': return { tipo: 'definitivo', mensagem: TEXTO_REQUER_ESTRUTURA };
    case 'requer-ciencia-falta': return { tipo: 'definitivo', mensagem: TEXTO_REQUER_FALTA };
    case 'conflito': return { tipo: 'conflito' };
    case 'incerto': return { tipo: 'incerto' };
    case 'definitivo': return { tipo: 'definitivo', mensagem: desfecho.mensagem };
    default: return null;
  }
}

export function LiberacaoMateriais({ opId, aberto, onAbertoChange, podeLiberar, preflight: preflightRecebido, onLiberada }: LiberacaoMateriaisProps): ReactElement {
  const [preflight, setPreflight] = useState<PreflightLiberacao | null>(preflightRecebido ?? null);
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<ErroLiberacao | null>(null);
  const [cienciaFalta, setCienciaFalta] = useState(false);
  const [cienciaEstrutura, setCienciaEstrutura] = useState(false);
  const [tentativa, setTentativa] = useState<string | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const guardaRef = useRef<GuardaEnvio>({ emAndamento: false });

  useEffect(() => {
    const atualizar = (): void => setOnline(navigator.onLine);
    window.addEventListener('online', atualizar);
    window.addEventListener('offline', atualizar);
    return () => { window.removeEventListener('online', atualizar); window.removeEventListener('offline', atualizar); };
  }, []);

  const estado = estadoLiberacao({ carregando, enviando, online, erro, preflight, cienciaFalta, cienciaEstrutura, podeLiberar });

  /** Relê o preflight. Falhas de ciência/desenho recarregam mantendo o aviso do contrato. */
  const carregar = async (manterErro = false): Promise<void> => {
    if (preflightRecebido) { if (!manterErro) setErro(null); return; }
    setCarregando(true);
    if (!manterErro) setErro(null);
    try { setPreflight(await abastecimentoApi.preflightLiberacao(opId)); }
    catch { setErro({ tipo: 'leitura' }); }
    finally { setCarregando(false); }
  };

  useEffect(() => { if (aberto) void carregar(); }, [aberto, opId]);

  const confirmar = async (): Promise<void> => {
    if (!estado.ctaHabilitada) return;
    setEnviando(true); setErro(null);
    try {
      const retorno = await confirmarLiberacao({ api: abastecimentoApi, opId, ciencia: cienciaDoPreflight(preflight), chaveAtual: tentativa, guarda: guardaRef.current });
      if (retorno === null) return;
      setTentativa(retorno.chave);
      const { desfecho } = retorno;
      if (desfecho.tipo === 'liberada') { onAbertoChange(false); onLiberada?.(desfecho.resultado); return; }
      setErro(erroDoDesfecho(desfecho));
      if (desfecho.tipo === 'requer-ciencia-estrutura') setCienciaEstrutura(false);
      if (desfecho.tipo === 'requer-ciencia-falta') setCienciaFalta(false);
      // Ciência/desenho: a situação mudou no servidor, relê já. Conflito e incerto aguardam o botão
      // 'Recarregar situação atual' e nunca disparam novo POST sozinhos.
      if (desfecho.tipo === 'requer-ciencia-estrutura' || desfecho.tipo === 'requer-ciencia-falta' || desfecho.tipo === 'bloqueada-desenho') await carregar(true);
    } finally { setEnviando(false); }
  };

  return <FormDialog open={aberto} onOpenChange={onAbertoChange}>
    <FormDialogContent size="wide" aria-describedby={undefined}>
      <FormDialogHeader>
        <FormDialogTitle>Liberar ordem de produção</FormDialogTitle>
        <FormDialogDescription>Revise o desenho e a demanda. Falta e ciência não reservam material nem confirmam uma entrega.</FormDialogDescription>
      </FormDialogHeader>
      <FormDialogBody className="space-y-4">
        <LiberacaoCorpo
          preflight={preflight}
          carregando={carregando}
          enviando={enviando}
          online={online}
          podeLiberar={podeLiberar}
          erro={erro}
          cienciaFalta={cienciaFalta}
          cienciaEstrutura={cienciaEstrutura}
          onCienciaFalta={setCienciaFalta}
          onCienciaEstrutura={setCienciaEstrutura}
          onRecarregar={() => void carregar()}
          onTentarNovamente={() => void carregar()}
        />
      </FormDialogBody>
      <FormDialogFooter>
        <button type="button" onClick={() => onAbertoChange(false)} disabled={enviando} className="min-h-11 rounded border border-border px-4 font-semibold">Cancelar</button>
        <button type="button" onClick={() => void confirmar()} disabled={!estado.ctaHabilitada} aria-busy={enviando} aria-describedby={estado.bloqueio === 'OFFLINE' ? 'liberacao-offline' : undefined} className="inline-flex min-h-11 items-center gap-2 rounded bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50">
          {enviando ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : <CheckCircle2 aria-hidden="true" className="size-4" />}{enviando ? 'Liberando…' : estado.cta}
        </button>
      </FormDialogFooter>
    </FormDialogContent>
  </FormDialog>;
}
